import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiRequest } from "@/lib/queryClient";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Target,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  RefreshCcw,
  Download,
  BarChart3,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Sparkles,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Segment {
  id: number;
  name: string;
  description: string | null;
  userCount: number;
  isActive: boolean;
  createdAt: string;
}

interface QuestionMapping {
  id: number;
  questionId: number;
  segmentId: number;
  weight: string;
  positiveAnswers: string[];
  negativeAnswers: string[];
  question?: {
    id: number;
    text: string;
    category: string | null;
    subCategory: string | null;
    options?: string[] | null;
  };
  segment?: {
    id: number;
    name: string;
  };
}

interface UserInSegment {
  userId: number;
  username: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  confidence: string;
  assignedAt: string;
}

interface JobEntry {
  status: "idle" | "running" | "done";
  summary?: string;
  total?: number;
  processed?: number;
}

interface JobStatusData {
  insights: JobEntry;
  batch: JobEntry;
  clear: JobEntry;
}

interface AnswerInsightRow {
  questionId: number;
  questionText: string | null;
  category: string | null;
  subCategory: string | null;
  answerText: string;
  signal: string;
  weight: string | number | null;
  insightText?: string | null;
  derivedTrait?: string | null;
  traitCategory?: string | null;
  profilingUse?: string | null;
}

const JOB_LABELS: Record<"insights" | "batch" | "clear", string> = {
  insights: "Answer Insights",
  batch: "Batch Processing",
  clear: "Clear Segmentation",
};

export default function AdminSegmentation() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);
  const [usersPage, setUsersPage] = useState(1);
  const [newMappingOpen, setNewMappingOpen] = useState(false);
  const [pendingJob, setPendingJob] = useState<"insights" | "batch" | "clear" | null>(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summaryTitle, setSummaryTitle] = useState("");
  const [summaryText, setSummaryText] = useState("");
  const handleViewUsers = (segmentId: number) => {
    setSelectedSegment(segmentId);
    setActiveTab("segment-users");
  };

  // Fetch segments
  const { data: segmentsResponse, isLoading: segmentsLoading } = useQuery<{
    success: boolean;
    segments: Segment[];
    count: number;
  }>({
    queryKey: ["/api/v1/segments"],
    enabled: !!user,
  });
  const segmentsList = segmentsResponse?.segments ?? [];
  const topSegment =
    segmentsList.length > 0
      ? segmentsList.reduce<Segment | null>(
          (best, seg) =>
            !best || seg.userCount > best.userCount ? seg : best,
          null,
        )
      : null;

  const {
    data: insightsResponse,
    isLoading: insightsLoading,
  } = useQuery<{
    success: boolean;
    insights: AnswerInsightRow[];
  }>({
    queryKey: ["/api/v1/segments/admin/answer-insights"],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        "/api/v1/segments/admin/answer-insights",
      );
      return response.json();
    },
  });
  const insightsList = insightsResponse?.insights ?? [];
  const [insightsPage, setInsightsPage] = useState(1);
  const insightsPerPage = 5;

  const groupedInsights = useMemo(() => {
    const map = new Map<
      number,
      { question: AnswerInsightRow; answers: AnswerInsightRow[] }
    >();
    insightsList.forEach((row) => {
      const existing =
        map.get(row.questionId) || { question: row, answers: [] };
      existing.answers.push(row);
      map.set(row.questionId, existing);
    });
    return Array.from(map.values());
  }, [insightsList]);

  const paginatedInsights = useMemo(() => {
    const start = (insightsPage - 1) * insightsPerPage;
    return groupedInsights.slice(start, start + insightsPerPage);
  }, [groupedInsights, insightsPage]);

  const insightsTotalPages = Math.max(
    1,
    Math.ceil(groupedInsights.length / insightsPerPage),
  );

  const {
    data: jobStatus,
    refetch: refetchJobStatus,
  } = useQuery<JobStatusData>({
    queryKey: ["/api/v1/segments/admin/job-status"],
    enabled: !!user,
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/v1/segments/admin/job-status");
      return response.json();
    },
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!pendingJob || !jobStatus) {
      return;
    }
    const entry = jobStatus[pendingJob];
    if (entry?.status === "done") {
      setSummaryTitle(`${JOB_LABELS[pendingJob]} Complete`);
      if (pendingJob === "batch") {
        const processed = entry.processed ?? 0;
        const total = entry.total;
        setSummaryText(
          total && total > 0
            ? `Batch processing handled ${processed} of ${total} users`
            : `Batch processing handled ${processed} users`,
        );
      } else if (pendingJob === "insights") {
        const processed = entry.processed ?? 0;
        const total = entry.total;
        setSummaryText(
          total && total > 0
            ? `Generated insights for ${processed} of ${total} questions`
            : `Generated insights for ${processed} questions`,
        );
      } else {
        setSummaryText(
          entry.summary || `${JOB_LABELS[pendingJob]} job finished.`,
        );
      }
      setSummaryDialogOpen(true);
      setPendingJob(null);
    }
  }, [jobStatus, pendingJob]);

  // Fetch mappings
  const { data: mappingsResponse, isLoading: mappingsLoading } = useQuery<{
    success: boolean;
    mappings: QuestionMapping[];
    count: number;
  }>({
    queryKey: ["/api/v1/segments/admin/mappings"],
    enabled: !!user,
  });
  const mappingsList = mappingsResponse?.mappings ?? [];
  const [mappingsPage, setMappingsPage] = useState(1);
  const mappingsPerPage = 10;

  const aggregatedMappings = useMemo(() => {
    const byQuestion = new Map<
      number,
      {
        questionId: number;
        question: QuestionMapping["question"];
        segments: Set<string>;
        positiveAnswers: Set<string>;
      }
    >();

    mappingsList.forEach((mapping) => {
      const existing = byQuestion.get(mapping.questionId) ?? {
        questionId: mapping.questionId,
        question: mapping.question,
        segments: new Set<string>(),
        positiveAnswers: new Set<string>(),
      };

      if (mapping.segment?.name) {
        existing.segments.add(mapping.segment.name);
      }

      const options =
        mapping.question?.options && Array.isArray(mapping.question.options)
          ? mapping.question.options
          : mapping.positiveAnswers || [];

      options.forEach((answer) => {
        if (answer && String(answer).trim()) {
          existing.positiveAnswers.add(String(answer).trim());
        }
      });

      byQuestion.set(mapping.questionId, existing);
    });

    return Array.from(byQuestion.values()).map((entry) => ({
      questionId: entry.questionId,
      question: entry.question,
      segments: Array.from(entry.segments),
      positiveAnswers: Array.from(entry.positiveAnswers),
    }));
  }, [mappingsList]);

  const paginatedMappings = useMemo(() => {
    const start = (mappingsPage - 1) * mappingsPerPage;
    return aggregatedMappings.slice(start, start + mappingsPerPage);
  }, [aggregatedMappings, mappingsPage]);

  const mappingsTotalPages = Math.max(
    1,
    Math.ceil(aggregatedMappings.length / mappingsPerPage),
  );

  useEffect(() => {
    setUsersPage(1);
  }, [selectedSegment]);

  const { data: usersData, isLoading: usersLoading } = useQuery<{
    success: boolean;
    users: UserInSegment[];
    total: number;
    page: number;
    totalPages: number;
  } | null>({
    queryKey: ["segment-users", selectedSegment, usersPage],
    enabled: !!selectedSegment,
    keepPreviousData: true,
    queryFn: async () => {
      if (!selectedSegment) return null;
      const response = await apiRequest("GET", `/api/v1/segments/${selectedSegment}/users?page=${usersPage}`);
      return response.json();
    },
  });

  // Batch process mutation
  const batchProcessMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/v1/segments/batch-process", {
        segmentAll: true,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Batch Processing Initiated",
        description: "Users are being processed in the background.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/segments"] });
      setPendingJob("batch");
      refetchJobStatus();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const cancelBatchMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        "/api/v1/segments/admin/batch-cancel",
      );
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Batch Cancellation Requested",
        description:
          data.message ||
          "The current batch job will stop after the current batch completes.",
      });
      refetchJobStatus();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete mapping mutation
  const deleteMappingMutation = useMutation({
    mutationFn: async (mappingId: number) => {
      const response = await apiRequest("DELETE", `/api/v1/segments/admin/mappings/${mappingId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Mapping Deleted",
        description: "Question mapping has been removed.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/segments/admin/mappings"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const answerInsightsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/v1/segments/admin/answer-insights");
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Answer Insights Job Queued",
        description: data.message || "Insights are being generated in the background.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/segments/admin/mappings"] });
      setPendingJob("insights");
      refetchJobStatus();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  const clearSegmentationMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/v1/segments/admin/clear-segmentation");
      return response.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: "Segmentation Cleared",
        description: data.summary || "All segment assignments were cleared.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/v1/segments"] });
      setPendingJob("clear");
      refetchJobStatus();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const getSegmentColor = (index: number) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-yellow-500",
      "bg-red-500",
      "bg-teal-500",
      "bg-cyan-500",
      "bg-lime-500",
      "bg-amber-500",
    ];
    return colors[index % colors.length];
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Please log in to access this page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">User Segmentation Dashboard</h1>
          <p className="text-muted-foreground">
            Manage answer-based user segments and question mappings
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => answerInsightsMutation.mutate()}
            disabled={answerInsightsMutation.isPending}
            variant="outline"
          >
            {answerInsightsMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating insights...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Trigger Answer Insights
              </>
            )}
          </Button>
          <Button
            onClick={() => batchProcessMutation.mutate()}
            disabled={
              batchProcessMutation.isPending ||
              jobStatus?.batch?.status === "running"
            }
            variant="outline"
          >
            {batchProcessMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <RefreshCcw className="mr-2 h-4 w-4" />
                Batch Process Users
              </>
            )}
          </Button>
          {jobStatus?.batch?.status === "running" && (
            <Button
              onClick={() => cancelBatchMutation.mutate()}
              disabled={cancelBatchMutation.isPending}
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive/10"
            >
              {cancelBatchMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Stopping...
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Stop Batch
                </>
              )}
            </Button>
          )}
          <Button
            onClick={() => clearSegmentationMutation.mutate()}
            disabled={clearSegmentationMutation.isPending}
            variant="outline"
            className="border-destructive text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Clear Segmentation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Segments</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
        <div className="text-2xl font-bold">{segmentsList.length}</div>
            <p className="text-xs text-muted-foreground">User categories</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users Segmented</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {segmentsList.reduce((sum, seg) => sum + (seg.userCount || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">Across all segments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Question Mappings</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mappingsList.length}</div>
            <p className="text-xs text-muted-foreground">Active mappings</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Segment</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {topSegment ? topSegment.name : "N/A"}
            </div>
            <p className="text-xs text-muted-foreground">
              {topSegment
                ? `${topSegment.userCount} users assigned`
                : "No data"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Batch processing progress */}
      {jobStatus?.batch && jobStatus.batch.status !== "idle" && (
        <div className="rounded-md border p-3 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {jobStatus.batch.status === "running"
                ? "Batch processing users"
                : "Batch processing complete"}
            </span>
            <span className="text-xs text-muted-foreground">
              {jobStatus.batch.processed ?? 0}{" "}
              {typeof jobStatus.batch.total === "number"
                ? `/ ${jobStatus.batch.total} users`
                : "users processed"}
            </span>
          </div>
          <Progress
            value={Math.min(
              100,
              typeof jobStatus.batch.total === "number" &&
                jobStatus.batch.total > 0
                ? ((jobStatus.batch.processed ?? 0) /
                    jobStatus.batch.total) *
                  100
                : 0,
            )}
          />
        </div>
      )}

      {/* Answer Insights progress */}
      {jobStatus?.insights &&
        jobStatus.insights.status !== "idle" && (
          <div className="rounded-md border p-3 flex flex-col gap-2 mt-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Generating answer insights</span>
              <span className="text-xs text-muted-foreground">
                {jobStatus.insights.processed ?? 0}{" "}
                {typeof jobStatus.insights.total === "number"
                  ? `/ ${jobStatus.insights.total} questions`
                  : "questions processed"}
              </span>
            </div>
            <Progress
              value={Math.min(
                100,
                typeof jobStatus.insights.total === "number" &&
                  jobStatus.insights.total > 0
                  ? ((jobStatus.insights.processed ?? 0) /
                      jobStatus.insights.total) *
                    100
                  : 0,
              )}
            />
          </div>
        )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Segments Overview</TabsTrigger>
          <TabsTrigger value="mappings">Question Mappings</TabsTrigger>
          <TabsTrigger value="insights">AI Answer Insights</TabsTrigger>
          <TabsTrigger value="segment-users">Segment Users</TabsTrigger>
        </TabsList>

        {/* Segments Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {segmentsLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {segmentsList.map((segment, index) => (
                <Card key={segment.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getSegmentColor(index)}`} />
                        <CardTitle className="text-lg">{segment.name}</CardTitle>
                      </div>
                      <Badge variant={segment.isActive ? "default" : "secondary"}>
                        {segment.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <CardDescription className="line-clamp-2">
                      {segment.description || "No description available"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Users:</span>
                        <span className="text-2xl font-bold">{segment.userCount}</span>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleViewUsers(segment.id)}
                      >
                        <Users className="mr-2 h-4 w-4" />
                        View Users
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Question Mappings Tab */}
        <TabsContent value="mappings" className="space-y-4">
          <div className="flex justify-between items-center">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Question Mappings</AlertTitle>
              <AlertDescription>
                Defines which questions contribute to which user segments. Each mapping includes positive/negative answer patterns and weights.
              </AlertDescription>
            </Alert>
          </div>

          {mappingsLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : aggregatedMappings.length > 0 ? (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead>Segments</TableHead>
                      <TableHead>Answers</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedMappings.map((mapping) => (
                      <TableRow key={mapping.questionId}>
                        <TableCell className="max-w-md">
                          <div>
                            <p className="font-medium line-clamp-1">
                              {mapping.question?.text || `Question #${mapping.questionId}`}
                            </p>
                            {mapping.question?.category && (
                              <p className="text-xs text-muted-foreground">
                                {mapping.question.category}
                                {mapping.question.subCategory && ` › ${mapping.question.subCategory}`}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {mapping.segments.map((name) => (
                              <Badge key={name} variant="outline" className="text-xs">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {mapping.positiveAnswers.slice(0, 3).map((answer, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {answer}
                              </Badge>
                            ))}
                            {mapping.positiveAnswers.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{mapping.positiveAnswers.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {mappingsTotalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
                    <span className="text-muted-foreground">
                      Page {mappingsPage} of {mappingsTotalPages}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMappingsPage((p) => Math.max(1, p - 1))}
                        disabled={mappingsPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setMappingsPage((p) =>
                            Math.min(mappingsTotalPages, p + 1),
                          )
                        }
                        disabled={mappingsPage === mappingsTotalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No mappings found</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Run the POC script to create question mappings
                </p>
                <code className="bg-muted px-3 py-1 rounded text-sm">
                  tsx scripts/create-poc-segment-mappings.ts
                </code>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Answer Insights Tab */}
        <TabsContent value="insights" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Answer Insights</CardTitle>
              <CardDescription>
                How individual answer options translate into traits and profiling signals used for segmentation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {insightsLoading ? (
                <div className="flex justify-center p-6">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : insightsList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center text-sm text-muted-foreground">
                  <AlertCircle className="h-10 w-10 mb-3" />
                  <p className="font-medium mb-1">No AI answer insights found</p>
                  <p className="mb-3">
                    Trigger the <span className="font-semibold">Answer Insights</span> job to generate insights for questions.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => answerInsightsMutation.mutate()}
                    disabled={answerInsightsMutation.isPending}
                  >
                    {answerInsightsMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating insights...
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Trigger Answer Insights
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {paginatedInsights.map(({ question, answers }) => (
                    <div key={question.questionId} className="border rounded-lg p-4 space-y-2">
                      <div>
                        <p className="font-medium">
                          {question.questionText || `Question #${question.questionId}`}
                        </p>
                        {(question.category || question.subCategory) && (
                          <p className="text-xs text-muted-foreground">
                            {question.category}
                            {question.subCategory && ` › ${question.subCategory}`}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {answers.map((row, idx) => (
                          <div
                            key={`${row.questionId}-${idx}`}
                            className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline">{row.answerText}</Badge>
                              <Badge
                                variant={
                                  row.signal === "positive"
                                    ? "default"
                                    : row.signal === "negative"
                                    ? "destructive"
                                    : "secondary"
                                }
                                className="text-xs"
                              >
                                {row.signal}
                              </Badge>
                              {row.derivedTrait && (
                                <Badge variant="outline" className="text-xs">
                                  Trait: {row.derivedTrait}
                                </Badge>
                              )}
                              {row.traitCategory && (
                                <Badge variant="outline" className="text-xs">
                                  {row.traitCategory}
                                </Badge>
                              )}
                            </div>
                            <div className="md:text-right text-xs text-muted-foreground max-w-xl">
                              {row.profilingUse || row.insightText || "No insight text available"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {insightsTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-2 border-t text-sm">
                      <span className="text-muted-foreground">
                        Page {insightsPage} of {insightsTotalPages}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setInsightsPage((p) => Math.max(1, p - 1))
                          }
                          disabled={insightsPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setInsightsPage((p) =>
                              Math.min(insightsTotalPages, p + 1),
                            )
                          }
                          disabled={insightsPage === insightsTotalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="segment-users" className="space-y-4">
          {!selectedSegment ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Select a segment to view users</p>
                <p className="text-sm text-muted-foreground">
                  Click “View Users” on any segment card to inspect the assigned users.
                </p>
              </CardContent>
            </Card>
          ) : usersLoading && !usersData ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : usersData ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  Users in {segmentsList.find((s) => s.id === selectedSegment)?.name}
                </CardTitle>
                <CardDescription>
                  Showing page {usersData.page} of {usersData.totalPages}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Assigned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usersData.users.map((user: UserInSegment) => {
                      const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
                      const assignedDate = user.assignedAt ? new Date(user.assignedAt) : null;
                      return (
                        <TableRow key={user.userId}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{fullName || user.username}</p>
                            </div>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge variant={Number(user.confidence) >= 0.7 ? "default" : "secondary"}>
                              {(Number(user.confidence) * 100).toFixed(0)}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {assignedDate ? assignedDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "N/A"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {usersData.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {usersPage} of {usersData.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage((p) => Math.min(usersData.totalPages, p + 1))}
                      disabled={usersPage === usersData.totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No users found for this segment</p>
                <p className="text-sm text-muted-foreground">
                  Run batch processing to populate segment assignments.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
      <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{summaryTitle || "Job Summary"}</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {summaryText || "The triggered job has completed."}
          </DialogDescription>
          <DialogFooter>
            <Button onClick={() => setSummaryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
